// tracking_scripts/tag_manager/product.ts

/**
 * Interface representing the details required to initialize a product.
 */
interface ProductDetails {
	id: string;
	category?: string;
	sku_code?: string;
	name?: string;
	unit_sale_price?: string;
	unit_price?: string;
	manufacturer?: string;
	wine_category?: string;
	subcategory?: string;
	grape_type_web?: string;
	country?: string;
	brand?: string;
	vegetarian?: boolean;
	vegan?: boolean;
	case_category?: string;
	case_size?: string;
	product_location?: string;
	spirits_category?: string;
	beer_style?: string;
	container?: string;
	size?: string;
	beer_case_type?: string;
	[key: string]: any;
}

/**
 * Class representing a GTM product.
 */
export class GTM_Product {
	private product: any;
	private details: ProductDetails;
	private qty: number;

	/**
	 * Creates a new GTM_Product instance.
	 * First creates a basic universal object, then populates common and specific elements.
	 *
	 * @param {ProductDetails} productDetails - Product details from universal_variable or a basic template.
	 * @param {number} qty - Initial quantity of the product.
	 *
	 * @example
	 * // Create a new GTM_Product instance for a wine bottle
	 * const productDetails = { id: '123', category: 'Wine Bottles', name: 'Chardonnay', unit_sale_price: '12.99' };
	 * const wineProduct = new GTM_Product(productDetails, 1);
	 */
	constructor(productDetails: ProductDetails, qty: number) {
		this.details = productDetails;
		this.qty = qty;
		this.product = this.initializeProduct();
		if (this.details.category) {
			this._setCommon();
			this._setSpecifics(this.details.category);
		}
	}

	/**
	 * Initializes a new product object with default values.
	 * @returns {any} The initialized product object.
	 * @private
	 */
	private initializeProduct(): any {
		return {
			product_id: '',
			product_name: '',
			item_id: '',
			item_store_id: this.details.id,
			item_name: '',
			item_brand: '',
			item_category: this.details.category || '',
			item_category2: '',
			item_category3: '',
			item_category4: '',
			item_category5: '',
			price: '',
			discount: '',
			quantity: this.qty,
		};
	}

	/**
	 * Populates the new product with common elements.
	 * @private
	 */
	private _setCommon(): void {
		this.product.product_id = this.details.sku_code || '';
		this.product.product_name = this.details.name || '';
		this.product.item_id = this.details.sku_code || '';
		this.product.item_name = this.details.name || '';
		this.product.price = this.details.unit_sale_price || '';
		this.product.discount = (
			parseFloat(this.details.unit_price || '0') -
			parseFloat(this.details.unit_sale_price || '0')
		).toFixed(2);
	}

	/**
	 * Populates the new product with specific elements based on its category.
	 * Determines which data to populate by comparing the product's category.
	 * @param {string} category - The category retrieved from the universal_variable.
	 * @private
	 */
	private _setSpecifics(category: string): void {
		switch (category) {
			case 'Wine Bottles':
				this.product.item_brand = this.details.manufacturer || '';
				this.product.item_category2 = this.details.wine_category || '';
				this.product.item_category3 = this.details.subcategory || '';
				this.product.item_category4 = this.details.grape_type_web || '';
				this.product.item_category5 = this.details.country || '';
				break;
			case 'Pre-mixed Wine Cases':
				this.product.item_brand = this.details.brand || '';
				this.product.item_category2 = this.details.case_category || '';
				this.product.item_category3 = this.details.subcategory || '';
				this.product.item_category4 = this.details.case_size || '';
				this.product.item_category5 = this.details.product_location || '';
				break;
			case 'Spirits':
				this.product.item_brand = this.details.brand || '';
				this.product.item_category2 = this.details.spirits_category || '';
				this.product.item_category3 = this.details.subcategory || '';
				this.product.item_category4 =
					this.details[this.details.subcategory?.toLowerCase() + '_style'] || '';
				this.product.item_category5 = this.details.size || '';
				break;
			case 'Beers':
				this.product.item_brand = this.details.brewery || '';
				this.product.item_category2 = this.details.subcategory || '';
				this.product.item_category3 = this.details.beer_style || '';
				this.product.item_category4 = this.details.container || '';
				this.product.item_category5 = this.details.size || '';
				break;
			case 'Composite Beers':
				this.product.item_brand = this.details.brewery || '';
				this.product.item_category2 = this.details.beer_case_type || '';
				this.product.item_category3 = this.details.beer_style || '';
				this.product.item_category4 = this.details.container || '';
				this.product.item_category5 = this.details.product_location || '';
				break;
			case 'Composite Gifts':
				this.product.item_brand = this.details.brand || '';
				this.product.item_category2 = category;
				this.product.item_category3 = category;
				this.product.item_category4 = category;
				this.product.item_category5 = this.details.product_location || '';
				break;
			case 'Personalised Composite Gifts':
				this.product.item_brand = this.details.brand || '';
				this.product.item_category2 = category;
				this.product.item_category3 = category;
				this.product.item_category4 = category;
				this.product.item_category5 = this.details.product_location || '';
				break;
			default:
				break;
		}
	}

	/**
	 * Returns the new product object for use outside the class.
	 * @returns {any} The product object.
	 */
	getProduct(): any {
		return this.product;
	}
}
